<template>
  <div class="modal fade" id="rejectDisputeModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-close-circle-outline me-2"></i>
            Reject Dispute - {{ disputeDetail.disputeId }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Dispute Summary -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="alert alert-warning">
                <h6 class="alert-heading mb-2">
                  <i class="mdi mdi-alert-triangle me-2"></i>
                  Dispute Rejection Confirmation
                </h6>
                <div class="row">
                  <div class="col-md-4">
                    <p class="mb-1"><strong>Customer:</strong> {{ disputeDetail.customerName }}</p>
                    <p class="mb-1"><strong>Type:</strong> {{ formatDisputeType(disputeDetail.disputeType) }}</p>
                    <p class="mb-0"><strong>Reference:</strong> {{ disputeDetail.referenceNo }}</p>
                  </div>
                  <div class="col-md-4">
                    <p class="mb-1"><strong>Amount:</strong> {{ formatPrice(disputeDetail.disputeAmount) }}</p>
                    <p class="mb-1"><strong>Priority:</strong>
                      <span class="badge" :class="getPriorityClass(disputeDetail.priority)">{{ disputeDetail.priority }}</span>
                    </p>
                    <p class="mb-0"><strong>Date Raised:</strong> {{ formatDate(disputeDetail.dateRaised) }}</p>
                  </div>
                  <div class="col-md-4">
                    <p class="mb-1"><strong>Days Open:</strong> {{ getDaysOpen(disputeDetail.dateRaised) }} days</p>
                    <p class="mb-1"><strong>Assigned To:</strong> {{ disputeDetail.assignedTo || 'Unassigned' }}</p>
                    <p class="mb-0"><strong>Current Status:</strong>
                      <span class="badge bg-info">{{ formatStatus(disputeDetail.status) }}</span>
                    </p>
                  </div>
                </div>
                <hr class="my-3">
                <p class="mb-0 text-warning">
                  <strong>Warning:</strong> Rejecting this dispute will close it permanently. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          <!-- Rejection Form -->
          <form @submit.prevent="rejectDispute">
            <div class="row">
              <!-- Rejection Reason -->
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Rejection Details</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Primary Rejection Reason <span class="text-danger">*</span></label>
                      <select v-model="rejectionForm.primaryReason" @change="handleReasonChange"
                              class="form-select" required>
                        <option value="">Select primary reason...</option>
                        <option value="INSUFFICIENT_EVIDENCE">Insufficient Evidence</option>
                        <option value="CUSTOMER_ERROR">Customer Error/Misunderstanding</option>
                        <option value="CHARGES_CORRECT">Charges Are Correct</option>
                        <option value="OUTSIDE_POLICY">Outside Policy Guidelines</option>
                        <option value="DUPLICATE_DISPUTE">Duplicate Dispute</option>
                        <option value="FRIVOLOUS_CLAIM">Frivolous/Invalid Claim</option>
                        <option value="MISSING_DOCUMENTATION">Missing Required Documentation</option>
                        <option value="TIMEFRAME_EXCEEDED">Dispute Timeframe Exceeded</option>
                        <option value="PAYMENT_VERIFIED">Payment Already Verified</option>
                        <option value="TECHNICAL_LIMITATION">Technical System Limitation</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>

                    <div v-if="rejectionForm.primaryReason === 'OTHER'" class="mb-3">
                      <label class="form-label">Specify Other Reason <span class="text-danger">*</span></label>
                      <input v-model="rejectionForm.customReason" type="text" class="form-control"
                             placeholder="Please specify the rejection reason" required>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Secondary Reasons (Optional)</label>
                      <div class="form-check-group">
                        <div class="form-check">
                          <input v-model="rejectionForm.secondaryReasons" class="form-check-input"
                                 type="checkbox" value="INCOMPLETE_INFO" id="reason1">
                          <label class="form-check-label" for="reason1">Incomplete Information</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.secondaryReasons" class="form-check-input"
                                 type="checkbox" value="POLICY_COMPLIANCE" id="reason2">
                          <label class="form-check-label" for="reason2">Policy Compliance Issue</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.secondaryReasons" class="form-check-input"
                                 type="checkbox" value="VERIFICATION_FAILED" id="reason3">
                          <label class="form-check-label" for="reason3">Verification Failed</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.secondaryReasons" class="form-check-input"
                                 type="checkbox" value="CUSTOMER_UNRESPONSIVE" id="reason4">
                          <label class="form-check-label" for="reason4">Customer Unresponsive</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.secondaryReasons" class="form-check-input"
                                 type="checkbox" value="CONFLICTING_EVIDENCE" id="reason5">
                          <label class="form-check-label" for="reason5">Conflicting Evidence</label>
                        </div>
                      </div>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Rejection Category</label>
                      <select v-model="rejectionForm.rejectionCategory" class="form-select">
                        <option value="INVALID_DISPUTE">Invalid Dispute</option>
                        <option value="POLICY_VIOLATION">Policy Violation</option>
                        <option value="INSUFFICIENT_MERIT">Insufficient Merit</option>
                        <option value="PROCEDURAL_ERROR">Procedural Error</option>
                        <option value="CUSTOMER_FAULT">Customer Fault</option>
                        <option value="SYSTEM_LIMITATION">System Limitation</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Investigation Summary -->
            <div class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Investigation Summary</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Investigation Findings <span class="text-danger">*</span></label>
                      <textarea v-model="rejectionForm.investigationFindings" class="form-control" rows="4"
                                placeholder="Describe the investigation process and findings that led to this rejection..." required></textarea>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Evidence Reviewed</label>
                      <div class="form-check-group">
                        <div class="form-check">
                          <input v-model="rejectionForm.evidenceReviewed" class="form-check-input"
                                 type="checkbox" value="BILLING_RECORDS" id="evidence1">
                          <label class="form-check-label" for="evidence1">Billing Records</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.evidenceReviewed" class="form-check-input"
                                 type="checkbox" value="PAYMENT_RECORDS" id="evidence2">
                          <label class="form-check-label" for="evidence2">Payment Records</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.evidenceReviewed" class="form-check-input"
                                 type="checkbox" value="CUSTOMER_DOCUMENTS" id="evidence3">
                          <label class="form-check-label" for="evidence3">Customer Documents</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.evidenceReviewed" class="form-check-input"
                                 type="checkbox" value="SYSTEM_LOGS" id="evidence4">
                          <label class="form-check-label" for="evidence4">System Logs</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.evidenceReviewed" class="form-check-input"
                                 type="checkbox" value="POLICY_DOCUMENTS" id="evidence5">
                          <label class="form-check-label" for="evidence5">Policy Documents</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.evidenceReviewed" class="form-check-input"
                                 type="checkbox" value="EXTERNAL_VERIFICATION" id="evidence6">
                          <label class="form-check-label" for="evidence6">External Verification</label>
                        </div>
                      </div>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Supporting Documentation</label>
                      <textarea v-model="rejectionForm.supportingDocumentation" class="form-control" rows="3"
                                placeholder="List any documents or evidence that support the rejection decision..."></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Customer Communication -->
            <div class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Customer Communication</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <div class="form-check form-switch">
                        <input v-model="rejectionForm.notifyCustomer" class="form-check-input"
                               type="checkbox" id="notifyCustomer" checked>
                        <label class="form-check-label fw-bold" for="notifyCustomer">
                          Notify customer of rejection
                        </label>
                      </div>
                    </div>

                    <div v-if="rejectionForm.notifyCustomer">
                      <div class="mb-3">
                        <label class="form-label">Notification Method</label>
                        <div class="form-check-group">
                          <div class="form-check form-check-inline">
                            <input v-model="rejectionForm.notificationMethods" class="form-check-input"
                                   type="checkbox" value="EMAIL" id="notifyEmail">
                            <label class="form-check-label" for="notifyEmail">Email</label>
                          </div>
                          <div class="form-check form-check-inline">
                            <input v-model="rejectionForm.notificationMethods" class="form-check-input"
                                   type="checkbox" value="SMS" id="notifySMS">
                            <label class="form-check-label" for="notifySMS">SMS</label>
                          </div>
                          <div class="form-check form-check-inline">
                            <input v-model="rejectionForm.notificationMethods" class="form-check-input"
                                   type="checkbox" value="PHONE" id="notifyPhone">
                            <label class="form-check-label" for="notifyPhone">Phone Call</label>
                          </div>
                          <div class="form-check form-check-inline">
                            <input v-model="rejectionForm.notificationMethods" class="form-check-input"
                                   type="checkbox" value="LETTER" id="notifyLetter">
                            <label class="form-check-label" for="notifyLetter">Formal Letter</label>
                          </div>
                        </div>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Customer Message <span class="text-danger">*</span></label>
                        <textarea v-model="rejectionForm.customerMessage" class="form-control" rows="5"
                                  :placeholder="getDefaultCustomerMessage()" required></textarea>
                        <small class="text-muted">This message will be sent to the customer explaining the rejection</small>
                      </div>

                      <div class="mb-3">
                        <div class="form-check">
                          <input v-model="rejectionForm.includeAppealInfo" class="form-check-input"
                                 type="checkbox" id="includeAppeal" checked>
                          <label class="form-check-label" for="includeAppeal">
                            Include appeal process information in customer notification
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Appeal Information -->
            <div v-if="rejectionForm.includeAppealInfo" class="row mt-3">
              <div class="col-12">
                <div class="card border-info">
                  <div class="card-header bg-info text-white">
                    <h6 class="mb-0 text-white">
                      <i class="mdi mdi-gavel me-2"></i>
                      Appeal Process Information
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Appeal Deadline</label>
                      <select v-model="rejectionForm.appealDeadline" class="form-select">
                        <option value="7">7 Days</option>
                        <option value="14">14 Days</option>
                        <option value="30">30 Days</option>
                        <option value="60">60 Days</option>
                        <option value="90">90 Days</option>
                      </select>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Appeal Contact Information</label>
                      <textarea v-model="rejectionForm.appealContact" class="form-control" rows="3"
                                placeholder="Provide contact details for appeal submissions...">Appeals Department
Email: appeals@county.go.ke
Phone: +254-20-1234567
Address: County Appeals Office, P.O. Box 123, Nairobi</textarea>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Required Appeal Documentation</label>
                      <div class="form-check-group">
                        <div class="form-check">
                          <input v-model="rejectionForm.appealRequirements" class="form-check-input"
                                 type="checkbox" value="FORMAL_LETTER" id="req1" checked>
                          <label class="form-check-label" for="req1">Formal Appeal Letter</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.appealRequirements" class="form-check-input"
                                 type="checkbox" value="SUPPORTING_EVIDENCE" id="req2" checked>
                          <label class="form-check-label" for="req2">Supporting Evidence</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.appealRequirements" class="form-check-input"
                                 type="checkbox" value="ID_COPY" id="req3">
                          <label class="form-check-label" for="req3">Copy of ID/Registration</label>
                        </div>
                        <div class="form-check">
                          <input v-model="rejectionForm.appealRequirements" class="form-check-input"
                                 type="checkbox" value="APPEAL_FEE" id="req4">
                          <label class="form-check-label" for="req4">Appeal Processing Fee</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Internal Notes -->
            <div class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Internal Documentation</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Internal Notes</label>
                      <textarea v-model="rejectionForm.internalNotes" class="form-control" rows="3"
                                placeholder="Add any internal notes for team reference (not visible to customer)..."></textarea>
                      <small class="text-muted">These notes are for internal use only and will not be shared with the customer</small>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Quality Assurance</label>
                      <div class="form-check">
                        <input v-model="rejectionForm.qaApproved" class="form-check-input"
                               type="checkbox" id="qaApproved">
                        <label class="form-check-label" for="qaApproved">
                          This rejection has been reviewed and approved by supervisor
                        </label>
                      </div>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Follow-up Required</label>
                      <select v-model="rejectionForm.followUpRequired" class="form-select">
                        <option value="NONE">No Follow-up Required</option>
                        <option value="POLICY_REVIEW">Policy Review Needed</option>
                        <option value="SYSTEM_UPDATE">System Update Required</option>
                        <option value="TRAINING_NEEDED">Staff Training Needed</option>
                        <option value="PROCESS_IMPROVEMENT">Process Improvement</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Confirmation -->
            <div class="row mt-3">
              <div class="col-12">
                <div class="alert alert-danger">
                  <h6 class="alert-heading">Final Confirmation</h6>
                  <p class="mb-3">
                    By proceeding with this rejection, you confirm that:
                  </p>
                  <ul class="mb-3">
                    <li>The dispute has been thoroughly investigated</li>
                    <li>All relevant evidence has been reviewed</li>
                    <li>The rejection is justified and documented</li>
                    <li>The customer will be properly notified</li>
                    <li>Appeal process information has been provided (if applicable)</li>
                  </ul>
                  <div class="form-check">
                    <input v-model="rejectionForm.confirmRejection" class="form-check-input"
                           type="checkbox" id="confirmRejection" required>
                    <label class="form-check-label fw-bold" for="confirmRejection">
                      I confirm that I want to reject this dispute <span class="text-danger">*</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Submit Buttons -->
            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" :disabled="!canReject || rejecting" class="btn btn-danger">
                <span v-if="!rejecting">
                  <i class="mdi mdi-close-circle me-1"></i>
                  Reject Dispute
                </span>
                <span v-else>
                  <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                  Rejecting...
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from "moment";

export default {
  name: "RejectDisputeModal",
  props: {
    disputeDetail: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      rejecting: false,
      rejectionForm: {
        primaryReason: '',
        customReason: '',
        secondaryReasons: [],
        rejectionCategory: 'INSUFFICIENT_MERIT',
        investigationFindings: '',
        evidenceReviewed: [],
        supportingDocumentation: '',
        notifyCustomer: true,
        notificationMethods: ['EMAIL'],
        customerMessage: '',
        includeAppealInfo: true,
        appealDeadline: '30',
        appealContact: `Appeals Department
Email: appeals@county.go.ke
Phone: +254-20-1234567
Address: County Appeals Office, P.O. Box 123, Nairobi`,
        appealRequirements: ['FORMAL_LETTER', 'SUPPORTING_EVIDENCE'],
        internalNotes: '',
        qaApproved: false,
        followUpRequired: 'NONE',
        confirmRejection: false
      }
    }
  },
  computed: {
    canReject() {
      return this.rejectionForm.primaryReason &&
          this.rejectionForm.investigationFindings.trim() &&
          this.rejectionForm.confirmRejection &&
          (!this.rejectionForm.notifyCustomer || this.rejectionForm.customerMessage.trim());
    }
  },
  mounted() {
    this.rejectionForm.customerMessage = this.getDefaultCustomerMessage();
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    formatDate(dateString) {
      return moment(dateString).format('MMM DD, YYYY');
    },

    formatDisputeType(type) {
      const types = {
        'BILLING_ERROR': 'Billing Error',
        'PAYMENT_NOT_REFLECTED': 'Payment Not Reflected',
        'OVERCHARGE': 'Overcharge',
        'DUPLICATE_CHARGE': 'Duplicate Charge',
        'REFUND_REQUEST': 'Refund Request',
        'OTHER': 'Other'
      };
      return types[type] || type;
    },

    formatStatus(status) {
      const statuses = {
        'PENDING': 'Pending',
        'UNDER_REVIEW': 'Under Review',
        'RESOLVED': 'Resolved',
        'REJECTED': 'Rejected',
        'ESCALATED': 'Escalated'
      };
      return statuses[status] || status;
    },

    getPriorityClass(priority) {
      switch (priority) {
        case 'LOW':
          return 'bg-light text-dark';
        case 'MEDIUM':
          return 'bg-info';
        case 'HIGH':
          return 'bg-warning';
        case 'URGENT':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    getDaysOpen(dateString) {
      return moment().diff(moment(dateString), 'days');
    },

    handleReasonChange() {
      // Update customer message when reason changes
      this.rejectionForm.customerMessage = this.getDefaultCustomerMessage();
    },

    getDefaultCustomerMessage() {
      const customer = this.disputeDetail.customerName || 'Customer';
      const disputeId = this.disputeDetail.disputeId || '';
      const appealDays = this.rejectionForm.appealDeadline;

      let baseMessage = `Dear ${customer},\n\nRegarding your dispute ${disputeId}, we have completed our investigation and unfortunately must reject your dispute claim.\n\n`;

      switch (this.rejectionForm.primaryReason) {
        case 'INSUFFICIENT_EVIDENCE':
          baseMessage += 'Our investigation found insufficient evidence to support your dispute claim. The documentation provided does not demonstrate an error in our billing or payment processing.';
          break;
        case 'CUSTOMER_ERROR':
          baseMessage += 'Our investigation shows that the charges are correct as per our published rates and policies. It appears there may have been a misunderstanding regarding the applicable fees.';
          break;
        case 'CHARGES_CORRECT':
          baseMessage += 'After thorough review of your account and our billing records, we have confirmed that all charges are accurate and in accordance with our published fee structure.';
          break;
        case 'OUTSIDE_POLICY':
          baseMessage += 'Your dispute falls outside the scope of our dispute resolution policy guidelines. The request does not meet the criteria for dispute resolution as outlined in our terms and conditions.';
          break;
        case 'DUPLICATE_DISPUTE':
          baseMessage += 'Our records show that this matter has been previously addressed and resolved. We cannot process duplicate disputes for the same issue.';
          break;
        case 'FRIVOLOUS_CLAIM':
          baseMessage += 'After careful review, we have determined that this dispute lacks merit and does not warrant further investigation or adjustment.';
          break;
        case 'MISSING_DOCUMENTATION':
          baseMessage += 'Despite multiple requests, the required supporting documentation has not been provided. Without this documentation, we cannot proceed with your dispute.';
          break;
        case 'TIMEFRAME_EXCEEDED':
          baseMessage += 'Your dispute was submitted outside the allowable timeframe for dispute resolution as specified in our terms and conditions.';
          break;
        case 'PAYMENT_VERIFIED':
          baseMessage += 'Our investigation confirmed that your payment was properly processed and applied to your account. No payment discrepancy was found.';
          break;
        default:
          baseMessage += 'After thorough investigation, we have determined that your dispute cannot be resolved in your favor based on the available evidence and applicable policies.';
      }

      if (this.rejectionForm.includeAppealInfo) {
        baseMessage += `\n\nIf you disagree with this decision, you have the right to appeal within ${appealDays} days of receiving this notice. Please contact our Appeals Department for more information on the appeal process.`;
      }

      baseMessage += '\n\nWe appreciate your understanding and thank you for giving us the opportunity to address your concerns.\n\nSincerely,\nCustomer Service Team';

      return baseMessage;
    },

    async rejectDispute() {
      try {
        this.rejecting = true;

        // Validate form
        if (!this.validateRejection()) {
          this.rejecting = false;
          return;
        }

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Emit rejection event
        this.$emit('dispute-rejected', this.disputeDetail.disputeId, this.rejectionForm);

        // Show confirmation
        alert(`Dispute ${this.disputeDetail.disputeId} has been rejected successfully.`);

        // Close modal
        this.closeModal();

        this.rejecting = false;
      } catch (error) {
        this.rejecting = false;
        alert('Error rejecting dispute: ' + error.message);
      }
    },

    validateRejection() {
      if (!this.rejectionForm.primaryReason) {
        alert('Please select a primary rejection reason');
        return false;
      }

      if (this.rejectionForm.primaryReason === 'OTHER' && !this.rejectionForm.customReason.trim()) {
        alert('Please specify the custom rejection reason');
        return false;
      }

      if (!this.rejectionForm.investigationFindings.trim()) {
        alert('Please provide investigation findings');
        return false;
      }

      if (!this.rejectionForm.confirmRejection) {
        alert('Please confirm that you want to reject this dispute');
        return false;
      }

      if (this.rejectionForm.notifyCustomer) {
        if (this.rejectionForm.notificationMethods.length === 0) {
          alert('Please select at least one notification method');
          return false;
        }
        if (!this.rejectionForm.customerMessage.trim()) {
          alert('Please provide a customer message');
          return false;
        }
      }

      return true;
    },

    closeModal() {
      const modalElement = document.getElementById('rejectDisputeModal');
      if (modalElement) {
        const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
        if (bootstrapModal) {
          bootstrapModal.hide();
        }
      }
    }
  },
  watch: {
    // Update customer message when appeal info changes
    'rejectionForm.includeAppealInfo'() {
      this.rejectionForm.customerMessage = this.getDefaultCustomerMessage();
    },
    'rejectionForm.appealDeadline'() {
      this.rejectionForm.customerMessage = this.getDefaultCustomerMessage();
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 600;
  font-size: 0.875rem;
}

.form-check-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.form-check-group .form-check {
  margin-bottom: 0.5rem;
}

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.alert-heading {
  font-size: 1rem;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

.card-header.bg-light {
  background-color: #f8f9fa !important;
}

.card-header.bg-info h6 {
  color: white !important;
}

.border-info {
  border-color: #0dcaf0 !important;
}
</style>